import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  Grid,
  GridItem,
  Button,
  Progress,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  CircularProgress,
  CircularProgressLabel,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Select,
} from "@chakra-ui/react";
import { currentUser } from "utils/CurrentUser";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import axios from "axios";
import { ENDPOINTS } from "utils/Constants";
import { notify } from "components/ToastNotification";
import { NUMBER_OF_LESSONS, USER_LEVELS } from "utils/Constants";
import Card from "components/card/Card";

export default function Dashboard() {
  const navigate = useNavigate();
  const sidebarBg = useColorModeValue("gray.100", "gray.800");
  const contentBg = useColorModeValue("white", "gray.700");
  const textColor = useColorModeValue("gray.700", "white");
  const progressColor = useColorModeValue("teal.500", "teal.300");
  const [stats, setStats] = useState({});
  const [numLessons, setNumLessons] = useState(1);
  const [selectedLevel, setSelectedLevel] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(ENDPOINTS.GET_DASHBOARD_STATS, {
          headers: {
            Authorization: `Bearer ${currentUser.token}`,
          },
        });
        const responseData = response.data;
        if (!responseData.success) return notify(responseData.output);
        setStats(responseData.data);
      } catch (error) {
        notify("Could not fetch stats.", "error");
      }
    };
    fetchStats();
  }, [currentUser.token])

  const handleStartLearning = () => {
    onClose();
    navigate(`/admin/chat/${stats.unlearnedSkill?.id}?numLessons=${numLessons}&currentLevel=${selectedLevel}`);
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      {/* Main Content */}
      <Box flex="1"  p="20px" overflowY="auto">
        {/* Top Bar */}
        <Flex
          justify="space-between"
          align="center"
          mb="20px"
          p="10px"
          borderRadius="10px"
          boxShadow="md"
        >
          <Text fontSize="xl" fontWeight="bold">
            Welcome back, {currentUser?.firstName}!
          </Text>
          <Button variant="darkBrand" size="sm" onClick={() => navigate('/admin/explore-skills')}>
            Start New Skill
          </Button>
        </Flex>

        {/* Skill Overview Section */}
        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6}>
          <GridItem>
            <Box
              height={"100%"}
              p="20px"
              bg={useColorModeValue("gray.50", "gray.700")}
              borderRadius="10px"
              boxShadow="md"
            >
              {stats?.currentLearningSkill? (
                <>
                  <Text fontSize="lg" fontWeight="bold" mb="10px">
                    Current Skill: {stats?.currentLearningSkill?.name ?? "You're not learning anything yet."}
                  </Text>
                  <Progress value={stats?.currentLearningSkill?.percentage} variant="darkBrand" size="sm" />
                  <Text mt="5px" fontSize="sm">
                    {stats?.currentLearningSkill?.percentage ?? 0}% Complete
                  </Text>
                </>) : (
                <Text fontSize="lg" fontWeight="bold" mb="10px">
                  You're not learning anything yet
                </Text>
              )}

            </Box>
          </GridItem>

          <GridItem>
            <Box
              p="20px"
              bg={useColorModeValue("gray.50", "gray.700")}
              borderRadius="10px"
              boxShadow="md"
            >
              {stats.unlearnedSkill ? (
                <>
                  <Text fontSize="lg" fontWeight="bold" mb="10px">
                    Next Suggested Skill
                  </Text>
                  <Text fontSize="md" color={progressColor}>
                    {stats.unlearnedSkill?.name}
                  </Text>
                  <Button mt="10px" variant="darkBrand" size="sm" onClick={onOpen}>
                    Learn Now
                  </Button>
                </>
              ) : (
                <Text fontSize="lg" fontWeight="bold" mb="10px">
                  You're doing amazing!
                </Text>
              )}
            </Box>
          </GridItem>

          {/* Modal */}
          <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Start Learning</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Text mb="10px">Select the number of lessons:</Text>
                <Select
                  placeholder="Select number of lessons"
                  value={numLessons}
                  onChange={(e) => setNumLessons(e.target.value)}
                >
                  {NUMBER_OF_LESSONS.map((level, index) => (
                    <option key={index} value={level}>
                      {level}
                    </option>
                  ))}
                </Select>
                <Text mb="10px">Select your current level:</Text>
                <Select
                  placeholder="Select your level"
                  value={selectedLevel}
                  onChange={(e) => setSelectedLevel(e.target.value)}
                >
                  {USER_LEVELS.map((level, index) => (
                    <option key={index} value={level}>
                      {level}
                    </option>
                  ))}
                </Select>
              </ModalBody>
              <ModalFooter>
                <Button variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleStartLearning}
                  ml="10px"
                  isDisabled={!selectedLevel}
                >
                  Start
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </Grid>

        {/* Additional Statistics Section */}
        <Box
          mt="20px"
          p="20px"
          bg={useColorModeValue("gray.50", "gray.700")}
          borderRadius="10px"
          boxShadow="md"
        >
          <Text fontSize="lg" fontWeight="bold" mb="10px">
            Additional Statistics
          </Text>
          <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={6}>
            <GridItem>
              <Stat>
                <StatLabel>Total Skills Completed</StatLabel>
                <StatNumber>{stats.completedSkillsCount}</StatNumber>
                <StatHelpText>
                  <StatArrow type="increase" />
                  Keep up the good work
                </StatHelpText>
              </Stat>
            </GridItem>
            <GridItem>
              <Stat>
                <StatLabel>Ongoing skills</StatLabel>
                <StatNumber>{stats.ongoingSkillsCount}</StatNumber>
                <StatHelpText>
                  <StatArrow type="increase" />
                  Dont give up
                </StatHelpText>
              </Stat>
            </GridItem>
            <GridItem>
              <Stat>
                <StatLabel>Total points</StatLabel>
                <StatNumber>{stats.totalPoints}</StatNumber>
                <StatHelpText>
                  <StatArrow type="increase" />
                  Points earned from quizzes
                </StatHelpText>
              </Stat>
            </GridItem>
            <GridItem>
              <Stat>
                <StatLabel>User Rank</StatLabel>
                <StatNumber>#{stats.rank}</StatNumber>
                <StatHelpText>Out of {stats.totalUsers} learners</StatHelpText>
              </Stat>
            </GridItem>
          </Grid>
        </Box>

        {/* Performance Insights Section */}
        {/* Insights Section */}
        <Box
          mt="20px"
          p="20px"
          bg={useColorModeValue("gray.50", "gray.700")}
          borderRadius="10px"
          boxShadow="md"
        >
          <Text fontSize="lg" fontWeight="bold" mb="10px">
            Performance Insights
          </Text>
          <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
            <GridItem display={"flex"} flexDirection={"column"} alignContent={"center"} alignItems={"center"}>
              <CircularProgress value={stats.correctAnswersPercentage} size="120px" color={progressColor}>
                <CircularProgressLabel>{stats.correctAnswersPercentage}%</CircularProgressLabel>
              </CircularProgress>
              <Text mt="10px" fontSize="md" textAlign="left">
                Correct Answers (Overall)
              </Text>
            </GridItem>
            <GridItem display={"flex"} flexDirection={"column"} alignContent={"center"} alignItems={"center"}>
              <CircularProgress value={stats.incorrectAnswersPercentage} size="120px" color="red.500">
                <CircularProgressLabel>{stats.incorrectAnswersPercentage}%</CircularProgressLabel>
              </CircularProgress>
              <Text mt="10px" fontSize="md" textAlign="left">
                Skills Needing Improvement
              </Text>
            </GridItem>
            <GridItem display={"flex"} flexDirection={"column"} alignContent={"center"} alignItems={"center"}>
              <CircularProgress value={stats.averageQuizScore} size="120px" color="blue.400">
                <CircularProgressLabel>{stats.averageQuizScore}%</CircularProgressLabel>
              </CircularProgress>
              <Text mt="10px" fontSize="md" textAlign="left">
                Average Quiz Scores
              </Text>
            </GridItem>
          </Grid>
        </Box>

      </Box>
    </Box>
  );
}
